import React, { useEffect, createElement } from 'react';
import { motion } from 'framer-motion';
import { Check, ShieldCheck, Lock, CreditCard, ArrowRight, Star } from 'lucide-react';
import { trackPixelEvent } from './MetaPixel';
const CHECKOUT_BASE = 'https://megapackenfermeria.impultienda.ar/checkout';
// Fire Meta Pixel event without blocking navigation
function handleCheckoutClick() {
  try {
    trackPixelEvent('InitiateCheckout');
  } catch (_) {
    /* never block the redirect */
  }
}
export function PricingOptions() {
  // Warm up DNS + TLS to checkout origin so the redirect is instant on tap
  useEffect(() => {
    const head = document.head;
    const links: HTMLLinkElement[] = [];
    const add = (rel: string, href: string, crossOrigin?: boolean) => {
      const l = document.createElement('link');
      l.rel = rel;
      l.href = href;
      if (crossOrigin) l.crossOrigin = 'anonymous';
      head.appendChild(l);
      links.push(l);
    };
    add('preconnect', 'https://megapackenfermeria.impultienda.ar', true);
    add('dns-prefetch', 'https://megapackenfermeria.impultienda.ar');
    return () => {
      links.forEach(l => {
        if (l.parentNode) l.parentNode.removeChild(l);
      });
    };
  }, []);
  return <section id="pricing" className="py-8 md:py-20 lg:py-24 bg-clinical-deep text-white relative overflow-hidden" data-id="element-202">
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none" data-id="element-203">
        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(circle, #ffffff 1.5px, transparent 1.5px)',
        backgroundSize: '32px 32px'
      }} data-id="element-204" />
      </div>
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-clinical-mid/10 rounded-full blur-[120px] pointer-events-none" data-id="element-205"></div>

      <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-206">
        <div className="text-center max-w-3xl mx-auto mb-6 md:mb-14" data-id="element-207">
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-[10px] sm:text-xs font-semibold tracking-[0.22em] uppercase text-clinical-accent mb-3" data-id="element-208">
            Tu inversión
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-2xl sm:text-4xl md:text-5xl font-black text-white mb-2 md:mb-4 leading-[1.05] uppercase" data-id="element-209">
            Tu <span className="accent-italic-gold normal-case" data-id="element-210">Mega Pack</span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-sm md:text-lg text-white/70" data-id="element-211">
            Todo el sistema clínico en un solo lugar.{' '}
            <span className="accent-italic-gold" data-id="element-212">Una sola decisión.</span>
          </motion.p>
        </div>

        <div className="max-w-md mx-auto" data-id="element-213">
          {/* PACK CLÍNICO COMPLETO (Gold-themed) */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="relative bg-[#0A1F38] rounded-2xl md:rounded-3xl p-5 md:p-8 border border-clinical-accent/40 shadow-[0_16px_40px_rgba(217,177,74,0.10)] flex flex-col mt-4 md:mt-6" data-id="element-214">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-clinical-accent text-clinical-deep text-[9px] md:text-[10px] font-bold uppercase tracking-[0.22em] py-1 md:py-1.5 px-3 md:px-4 rounded-full whitespace-nowrap flex items-center gap-1.5" data-id="element-215">
              <Star size={10} className="fill-clinical-deep" strokeWidth={0} data-id="element-216" />
              Pack Clínico Completo
            </div>

            <div className="mt-1 md:mt-2" data-id="element-217">
              <h4 className="text-base md:text-2xl font-black text-white mb-1.5 md:mb-2 uppercase leading-tight" data-id="element-218">
                <span className="mr-1.5" data-id="element-219">📘</span>Mega Pack Enfermería
              </h4>
              <p className="text-clinical-accent/80 text-xs md:text-base mb-3 md:mb-5 font-medium" data-id="element-220">
                6 Tomos + 6 Bonos
              </p>
            </div>

            <div className="mb-2 md:mb-4" data-id="element-221">
              <div className="text-clinical-accent/40 line-through text-xs md:text-base font-medium mb-0.5 md:mb-1" data-id="element-222">
                $51.000
              </div>
              <div className="flex items-baseline gap-2" data-id="element-223">
                <span className="text-3xl md:text-5xl font-black text-white" data-id="element-224">
                  $21.990
                </span>
                <span className="text-xs md:text-base font-semibold text-white/50" data-id="element-225">
                  ARS
                </span>
              </div>
            </div>

            {/* Emotional value line */}
            <p className="text-[11px] md:text-sm text-white/70 italic mb-4 md:mb-6 leading-snug" data-id="element-226">
              Todo lo esencial para actuar con{' '}
              <span className="text-clinical-accent font-semibold not-italic" data-id="element-227">
                más seguridad clínica
              </span>
              .
            </p>

            <ul className="space-y-2.5 md:space-y-4 mb-4 md:mb-6 flex-grow" data-id="element-228">
              {['Acceso a los 6 Tomos completos', '6 Bonos de regalo (valor $53.000)', 'Actualizaciones de por vida sin costo', 'Ideal para prácticas, parciales e internado'].map((item, idx) => <li key={idx} className="flex items-start gap-2.5 md:gap-3" data-id="element-229">
                  <div className="w-5 h-5 md:w-6 md:h-6 rounded-full border border-clinical-accent/50 bg-clinical-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-230">
                    <Check className="w-3 h-3 md:w-3.5 md:h-3.5 text-clinical-accent" strokeWidth={3} data-id="element-231" />
                  </div>
                  <span className="text-white/90 text-xs md:text-base leading-snug" data-id="element-232">
                    {item}
                  </span>
                </li>)}
            </ul>

            <a href={CHECKOUT_BASE} target="_top" onClick={handleCheckoutClick} className="w-full py-3 md:py-4 rounded-full bg-clinical-accent/15 border border-clinical-accent/60 text-clinical-accent font-bold text-xs md:text-base hover:bg-clinical-accent/25 hover:border-clinical-accent/80 active:scale-[0.98] transition-all mt-auto uppercase tracking-wider flex items-center justify-center gap-2 touch-manipulation shadow-[0_8px_24px_rgba(217,177,74,0.18)] hover:shadow-[0_12px_28px_rgba(217,177,74,0.28)]" data-id="element-233">
              <ArrowRight size={14} data-id="element-234" />
              Quiero mi Mega Pack
            </a>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.3
      }} className="mt-6 md:mt-12 flex flex-wrap justify-center items-center gap-x-3 gap-y-1.5 text-[11px] sm:text-sm text-white/60 max-w-3xl mx-auto text-center" data-id="element-235">
          <div className="flex items-center gap-1.5" data-id="element-236">
            <ShieldCheck size={13} className="text-white/50" data-id="element-237" />
            <span data-id="element-238">Garantía 7 días</span>
          </div>
          <span className="text-white/20" data-id="element-239">·</span>
          <div className="flex items-center gap-1.5" data-id="element-240">
            <Lock size={13} className="text-white/50" data-id="element-241" />
            <span data-id="element-242">Pago seguro</span>
          </div>
          <span className="text-white/20" data-id="element-243">·</span>
          <div className="flex items-center gap-1.5" data-id="element-244">
            <CreditCard size={13} className="text-white/50" data-id="element-245" />
            <span data-id="element-246">Mercado Pago · Tarjeta</span>
          </div>
        </motion.div>
      </div>
    </section>;
}